import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  ChevronDown,
  ChevronRight,
  Pencil,
  Trash2,
  FileSpreadsheet,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Card, CardContent } from '@/core/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/core/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/core/components/ui/alert-dialog';
import { Badge } from '@/core/components/ui/badge';
import { Skeleton } from '@/core/components/ui/skeleton';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import { toast } from 'sonner';
import { useImportConfigurationStore } from '../../stores/import-configuration-store';
import { SheetMappingDialog } from '../organisms/SheetMappingDialog';
import { ColumnMappingsPanel } from '../organisms/ColumnMappingsPanel';
import { ColumnMappingDialog } from '../organisms/ColumnMappingDialog';
import {
  SheetMappingItem,
  formatColumnDisplay,
} from '../../types/import-configuration-types';
import { ApplicationTypeLabels } from '../../types/translation-types';

export function ImportConfiguration() {
  const navigate = useNavigate();
  const {
    sheetMappings,
    expandedMappingId,
    isLoading,
    isLoadingDetail,
    isSubmitting,
    error,
    deleteDialogOpen,
    itemToDelete,
    fetchSheetMappings,
    deleteSheetMapping,
    deleteColumnMapping,
    openSheetFormDialog,
    openDeleteDialog,
    closeDeleteDialog,
    setExpandedMappingId,
  } = useImportConfigurationStore();

  // Fetch on mount
  useEffect(() => {
    fetchSheetMappings();
  }, [fetchSheetMappings]);

  const handleAddSheetMapping = useCallback(() => {
    openSheetFormDialog();
  }, [openSheetFormDialog]);

  const handleEditSheetMapping = useCallback(
    (mapping: SheetMappingItem, e: React.MouseEvent) => {
      e.stopPropagation();
      openSheetFormDialog(mapping);
    },
    [openSheetFormDialog]
  );

  const handleDeleteSheetMapping = useCallback(
    (mapping: SheetMappingItem, e: React.MouseEvent) => {
      e.stopPropagation();
      openDeleteDialog('sheet', mapping.mappingId, mapping.sheetName);
    },
    [openDeleteDialog]
  );

  const handleToggleExpand = useCallback(
    (mapping: SheetMappingItem) => {
      if (expandedMappingId === mapping.mappingId) {
        setExpandedMappingId(null);
      } else {
        setExpandedMappingId(mapping.mappingId);
      }
    },
    [expandedMappingId, setExpandedMappingId]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!itemToDelete) return;

    try {
      if (itemToDelete.type === 'sheet') {
        await deleteSheetMapping(itemToDelete.id);
        toast.success(`Sheet mapping "${itemToDelete.name}" deleted`);
      } else {
        await deleteColumnMapping(itemToDelete.id);
        toast.success(`Column mapping "${itemToDelete.name}" deleted`);
      }
    } catch {
      toast.error('Failed to delete');
    }
  }, [itemToDelete, deleteSheetMapping, deleteColumnMapping]);

  const getExpandedMapping = useCallback(() => {
    if (!expandedMappingId) return null;
    return sheetMappings.find(m => m.mappingId === expandedMappingId) || null;
  }, [expandedMappingId, sheetMappings]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/translations')}
              className="h-8 w-8 p-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Import Configuration</h1>
              <p className="text-muted-foreground">
                Configure Excel sheet mappings and language column assignments
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => fetchSheetMappings()} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button onClick={handleAddSheetMapping}>
              <Plus className="h-4 w-4 mr-2" />
              Add Sheet Mapping
            </Button>
          </div>
        </div>

        {/* Info banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-800">
          <strong>Note:</strong> This configuration determines how Excel files are parsed during
          translation imports. Each sheet name must match exactly with the Excel file. Column
          numbers are 1-based (A=1, B=2, C=3...).
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-auto px-6 pb-4">
        {/* Error state */}
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={() => fetchSheetMappings()}>
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Loading state */}
        {isLoading && (
          <Card>
            <CardContent className="p-6 space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-6 w-6" />
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-6 w-40" />
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-6 w-20" />
                  <Skeleton className="h-6 w-16" />
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Empty state */}
        {!isLoading && !error && sheetMappings.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <FileSpreadsheet className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium mb-2">No sheet mappings configured</h3>
              <p className="text-muted-foreground mb-4">
                Add your first sheet mapping to configure how Excel files are imported.
              </p>
              <Button onClick={handleAddSheetMapping}>
                <Plus className="h-4 w-4 mr-2" />
                Add Sheet Mapping
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Data table */}
        {!isLoading && !error && sheetMappings.length > 0 && (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Sheet Name</TableHead>
                  <TableHead>Application Type</TableHead>
                  <TableHead>Key Column</TableHead>
                  <TableHead>Description Column</TableHead>
                  <TableHead>Languages</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sheetMappings.map(mapping => (
                  <>
                    {/* Main row */}
                    <TableRow
                      key={mapping.mappingId}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleToggleExpand(mapping)}
                    >
                      <TableCell>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                          {expandedMappingId === mapping.mappingId ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="font-medium">{mapping.sheetName}</TableCell>
                      <TableCell>
                        {ApplicationTypeLabels[mapping.applicationType] ||
                          `Type ${mapping.applicationType}`}
                      </TableCell>
                      <TableCell>{formatColumnDisplay(mapping.keyColumnNumber)}</TableCell>
                      <TableCell>
                        {mapping.descriptionColumnNumber
                          ? formatColumnDisplay(mapping.descriptionColumnNumber)
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {mapping.columnMappings?.length || 0} languages
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {mapping.isActive ? (
                          <Badge variant="default" className="bg-green-100 text-green-800">
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={e => handleEditSheetMapping(mapping, e)}
                            className="h-8 w-8 p-0"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={e => handleDeleteSheetMapping(mapping, e)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Expanded row - Column mappings */}
                    {expandedMappingId === mapping.mappingId && (
                      <TableRow key={`${mapping.mappingId}-expanded`}>
                        <TableCell colSpan={8} className="bg-gray-50 p-0">
                          <ColumnMappingsPanel
                            sheetMapping={getExpandedMapping()!}
                            isLoading={isLoadingDetail}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      {/* Sheet Mapping Dialog */}
      <SheetMappingDialog />

      {/* Column Mapping Dialog */}
      <ColumnMappingDialog />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={closeDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {itemToDelete?.type === 'sheet' ? 'Sheet Mapping' : 'Column Mapping'}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <strong>{itemToDelete?.name}</strong>?
              {itemToDelete?.type === 'sheet' && (
                <> This will also delete all associated language column mappings.</>
              )}
              {' '}This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isSubmitting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
